import { Component } from '@angular/core';
import { Braintree, PaymentUIOptions, PaymentUIResult } from '@ionic-native/braintree';
import { AlertController, LoadingController, NavController, NavParams } from 'ionic-angular';
import { Api } from '../../../../providers/api';
import { PostHardcopyPurchaseRequest } from '../../../../providers/api/requests/PostHardcopyPurchaseRequest';
import { HomePage } from '../../../home/home';
import { Country, CustomerDetails, ShippingAddress } from '../../interfaces';
import { HARDCOPY_COUNTRIES, HARDCOPY_DEFAULT_COUNTRY, HARDCOPY_PRICE, HARDCOPY_PRODUCT_NAME, BRAINTREE_TOKEN } from '../../settings';
import { formatPrice } from '../../tools';
import { HardcopyCustomerDetailsPage } from '../customer-details/customer-details';
import { HardcopyQuantityPage } from '../quantity/quantity';
import { HardcopyShippingAddressPage } from '../shipping-address/shipping-address';
import { FirebaseAnalytics } from '@ionic-native/firebase-analytics';

@Component({
    selector: 'page-hardcopy-order-total',
    templateUrl: 'order-total.html'
})

export class HardcopyOrderTotalPage {

    public productName = HARDCOPY_PRODUCT_NAME;
    public countries = HARDCOPY_COUNTRIES;
    public customerDetails: CustomerDetails = null;
    public qty: number = 1;
    public shippingAddress: ShippingAddress = null;

    public get country(): Country {
        let _default = null;
        for (let country of this.countries) {
            if (country.id == HARDCOPY_DEFAULT_COUNTRY) _default = country;
            if (country.id == this.shippingAddress.country) return country;
        }
        return _default;
    }

    public set country(country: Country) {
        this.shippingAddress.country = country.id;
    }

    constructor(
        public alertCtrl: AlertController,
        public api: Api,
        public analytics: FirebaseAnalytics,
        public braintree: Braintree,
        public loadingCtrl: LoadingController,
        public navCtrl: NavController,
        public navParams: NavParams,
    ) {
        this.customerDetails = navParams.get('customerDetails');
        this.shippingAddress = navParams.get('shippingAddress');
        this.qty = navParams.get('qty');
    }

    public ionViewDidEnter() {
        this.analytics.setCurrentScreen('HardcopyOrderTotal');
        this.analytics.logEvent('checkout_progress', {
            checkout_step: 5
        });
    }

    public async checkout() {
        let totalCost = this.getTotalCost();

        await this.braintree.initialize(BRAINTREE_TOKEN);

        let paymentUIOptions: PaymentUIOptions = {
            amount: totalCost.toFixed(2),
            primaryDescription: this.qty + 'x ' + this.productName
        };

        let paymentResult: PaymentUIResult;

        try {
            paymentResult = <PaymentUIResult> await this.braintree.presentDropInPaymentUI(paymentUIOptions);
        } catch (error) {
            console.error(error);
            this.showError('An error occured while attempting to authorise payment');
            return;
        }

        if (paymentResult.userCancelled) return;

        this.analytics.logEvent('add_payment_info', {});

        let order: PostHardcopyPurchaseRequest = {
            amount: totalCost,
            quantity: this.qty,
            nonce: paymentResult.nonce,
            customer_name: this.customerDetails.name,
            email: this.customerDetails.email,
            shipping_name: this.shippingAddress.name,
            address_1: this.shippingAddress.address1,
            city: this.shippingAddress.address2,
            county: this.shippingAddress.address3,
            country: this.country.id,
            postcode: this.shippingAddress.postcode,
        };

        let loading = this.loadingCtrl.create();
        loading.present();

        try {
            let result = await this.api.postHardcopyPurchase(order).toPromise();

            this.analytics.logEvent('ecommerce_purchase', {
                currency: 'EUR',
                item_category: 'hardcopy',
                item_id: 'hardcopy_2020',
                item_name: 'Ireland - A Directory 2020',
                quantity: order.quantity,
                value: order.amount,
            });

            let alert = this.alertCtrl.create({
                title: 'Success',
                message: 'You order has been accepted',
                buttons: [{
                    text: 'OK',
                    handler: () => {
                        this.navCtrl.setRoot(HomePage, null, {
                            animate: true,
                            direction: 'back'
                        });
                    }
                }]
            });

            alert.present();
        } catch (error) {
            if (error.error) {
                this.showError(error.error);
                return;
            }

            this.showError('An error occured while submitting your order');
        } finally {
            loading.dismiss();
        }
    }

    public formatPrice(price: number): string {
        return formatPrice(price);
    }

    public getAddress(): string {
        let shippingAddress = this.shippingAddress;
        let parts: string[] = [];
        if (shippingAddress.name) parts.push(shippingAddress.name);
        if (shippingAddress.address1) parts.push(shippingAddress.address1);
        if (shippingAddress.address2) parts.push(shippingAddress.address2);
        if (shippingAddress.address3) parts.push(shippingAddress.address3);
        if (this.country) parts.push(this.country.name);
        if (shippingAddress.postcode) parts.push(shippingAddress.postcode);
        return parts.join('<br>');
    }

    public getProductCost(): number {
        return HARDCOPY_PRICE * this.qty;
    }

    public getPostageCost() {
        return this.qty * this.country.postage;
    }

    public getTotalCost() {
        return this.getProductCost() + this.getPostageCost();
    }

    public showAlert(message: string, title: string) {
        let alert = this.alertCtrl.create({
            title: title,
            message: message,
            buttons: [{
                text: 'OK'
            }]
        });

        alert.present();
    }

    public showError(message: string) {
        this.showAlert(message, 'Error');
    }

    public editCustomerDetails() {
        this.navCtrl.push(HardcopyCustomerDetailsPage, {
            editMode: true,
            customerDetails: this.customerDetails,
            qty: this.qty,
            saveCallback: customerDetails => this.customerDetails = customerDetails,
        });
    }

    public editShippingAddress() {
        this.navCtrl.push(HardcopyShippingAddressPage, {
            editMode: true,
            customerDetails: this.customerDetails,
            qty: this.qty,
            saveCallback: shippingAddress => this.shippingAddress = shippingAddress,
            shippingAddress: this.shippingAddress,
        });
    }

    public editOrder() {
        this.navCtrl.push(HardcopyQuantityPage, {
            editMode: true,
            qty: this.qty,
            saveCallback: qty => this.qty = qty,
        });
    }

}



// WEBPACK FOOTER //
// ./src/pages/purchase-hardcopy/pages/order-total/order-total.ts